import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Image } from '@polaris-customerweb/sharedcomponents.shared';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
    CreateRideEvent,
    DeleteRideEvent,
    ForceEventsRefresh,
    SetRideEvents,
    UpdateRideEvent,
} from 'src/app/components/common/ride-event/ride-event.actions';
import {
    selectRideEvents,
    selectShouldForceEventsRefresh,
} from 'src/app/components/common/ride-event/ride-event.reducer';
import { AppState } from 'src/app/models/app-state';
import { Collection } from 'src/app/models/collection';
import { RideEvent } from 'src/app/models/ride-event';
import { ImageService } from 'src/app/services/image.service';
import { RideEventResource } from './ride-event-resource.service';

@Injectable({ providedIn: 'root' })
export class RideEventService {
    constructor(
        private imageService: ImageService,
        private resource: RideEventResource,
        private store: Store<AppState>
    ) {}

    // #region Get
    /**
     * @description Get ride events for logged in user (returns cached ride events for future requests).
     */
    getRideEvents$(): Observable<RideEvent[]> {
        return this.store.select(selectShouldForceEventsRefresh).pipe(
            switchMap((shouldForce) => {
                if (shouldForce) {
                    return this.resource.getRideEvents().pipe(
                        switchMap((rideEvents) => {
                            this.store.dispatch(new SetRideEvents(rideEvents));
                            this.store.dispatch(new ForceEventsRefresh(false));
                            return this.store.pipe(select(selectRideEvents));
                        })
                    );
                } else {
                    return this.store.pipe(select(selectRideEvents));
                }
            })
        );
    }

    getFullRideEvent(rideEventId: string): Observable<RideEvent> {
        return this.getRideEventFromStore(rideEventId).pipe(
            switchMap((rideEvent) => {
                return forkJoin([
                    of(rideEvent),
                    this.resource.getRideEventContents(rideEventId),
                ]);
            }),
            switchMap(([rideEvent, contentCollection]) => {
                rideEvent.setContentCollection(contentCollection);
                return forkJoin([
                    of(rideEvent),
                    this.getContentForAllRideEventCollections(
                        rideEventId,
                        rideEvent.collections
                    ),
                ]);
            }),
            map(([rideEvent, collections]) => {
                rideEvent.collections = collections;
                return rideEvent;
            })
        );
    }

    getFullRideEventWithCollection(
        rideEventId: string,
        collectionId: string
    ): Observable<RideEvent> {
        return this.getFullRideEvent(rideEventId).pipe(
            switchMap((rideEvent) => {
                return forkJoin([
                    of(rideEvent),
                    this.resource.getRideEventCollectionContents(
                        rideEventId,
                        collectionId
                    ),
                ]);
            }),
            map(([rideEvent, contentCollection]) => {
                const eventCollection = rideEvent.getCollection(collectionId);
                eventCollection?.setContentCollection(contentCollection);
                return rideEvent;
            })
        );
    }

    // #endregion

    // #region Create
    createRideEvent(
        rideEvent: RideEvent,
        coverImage?: File,
        filesToUpload?: File[]
    ): Observable<RideEvent> {
        return this.postRideEventImages(
            rideEvent,
            coverImage,
            filesToUpload
        ).pipe(
            switchMap((resultRideEvent) =>
                this.resource.create(resultRideEvent)
            ),
            tap((resultRideEvent) => {
                this.store.dispatch(new CreateRideEvent(resultRideEvent));
            }),
            tap(() => {
                this.store.dispatch(new ForceEventsRefresh(true));
            })
        );
    }

    // #endregion

    // #region Update
    updateRideEvent(
        rideEvent: RideEvent,
        coverImage?: File,
        filesToUpload?: File[],
        shouldDeleteCoverImage?: boolean
    ): Observable<RideEvent> {
        return this.postRideEventImages(
            rideEvent,
            coverImage,
            filesToUpload,
            shouldDeleteCoverImage
        ).pipe(
            switchMap((resultRideEvent) =>
                this.resource.update(resultRideEvent)
            ),
            tap((resultRideEvent) => {
                this.store.dispatch(new UpdateRideEvent(resultRideEvent));
            })
        );
    }

    // #endregion

    // #region Delete
    deleteRideEvent(rideEventId: string): Observable<null> {
        return this.resource.delete(rideEventId).pipe(
            tap(() => {
                this.store.dispatch(new DeleteRideEvent(rideEventId));
            })
        );
    }

    // //#endregion

    private getContentForAllRideEventCollections(
        rideEventId: string,
        collections: readonly Collection[]
    ): Observable<Collection[]> {
        if (collections.length === 0) {
            return of([]);
        }

        return forkJoin(
            collections.map((collection) => {
                return this.getContentsForRideEventCollection(
                    rideEventId,
                    collection
                );
            })
        );
    }

    private getContentsForRideEventCollection(
        rideEventId: string,
        collection: Collection
    ): Observable<Collection> {
        return this.resource
            .getRideEventCollectionContents(rideEventId, collection.id)
            .pipe(
                map(() => {
                    const resultCollection = collection.clone();
                    // TODO: CLI commented out. wrong type.
                    // resultCollection.setContentCollection(contentCollection);
                    return resultCollection;
                })
            );
    }

    private getRideEventFromStore(
        id: string,
        forceRefresh = false
    ): Observable<RideEvent> {
        if (!forceRefresh) {
            return this.getRideEvents$().pipe(
                take(1),
                switchMap((rideEvents) => {
                    const foundRideEvent = rideEvents.find(
                        (rideEvent) => rideEvent.id === id
                    );

                    if (!foundRideEvent) {
                        return this.getRideEventFromResource(id);
                    }

                    return of(foundRideEvent);
                })
            );
        }

        return this.getRideEventFromResource(id);
    }

    private getRideEventFromResource(id: string): Observable<RideEvent> {
        return this.resource.getRideEventById(id).pipe(
            tap((rideEvent) => {
                this.store.dispatch(new UpdateRideEvent(rideEvent));
            })
        );
    }

    private postRideEventImages(
        rideEvent: RideEvent,
        coverImage?: File,
        filesToUpload?: File[],
        shouldDeleteCoverImage?: boolean
    ): Observable<RideEvent> {
        const coverImageRequest$: Observable<Image | undefined> = coverImage
            ? this.imageService.uploadImageFile(coverImage)
            : of(undefined);
        const imageRequests$: Observable<Image>[] = filesToUpload
            ? filesToUpload.map((file) => {
                  return this.imageService.uploadImageFile(file);
              })
            : ([] as Observable<Image>[]);

        return forkJoin([coverImageRequest$, ...imageRequests$]).pipe(
            map(([coverImageItem, ...images]) => {
                if (coverImageItem || shouldDeleteCoverImage) {
                    rideEvent.coverImage = coverImageItem;
                }
                if (images.length > 0) {
                    rideEvent.images = [...rideEvent.images, ...images];
                }

                return rideEvent;
            })
        );
    }
}
